/**!
 * Created by Leonardo Cheslacki on 28/03/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

const HelpersCore = require('./components/helpers/HelpersCore');

const {make, default: {init: {single}, domain, iframe, routes: {web}}} = require('./../../config');

(function (global, factory) {
    'use strict';

    if (typeof (module) === 'object' && typeof (module.exports) === 'object') {
        /**
         *
         * For CommonJS and CommonJS-like environments where a proper `window`
         * is present, execute the factory and get ClassObject.
         * For environments that do not have a `window` with a `document`
         * (such as Node.js), expose a factory as module.exports.
         * This accentuates the need for the creation of a real `window`.
         * e.g. const ClassObject = require('Class')(window);
         * @type {void|(function(*): void)}
         */
        module.exports = global.document ? factory(global, false) : (_window) => {
            if (typeof (_window.document) !== 'undefined') {
                return factory(_window, true);
            } else {
                throw (new Error('Class requires a window with a document'));
            }
        };
    } else {
        factory(global, true);
    }
})(typeof (window) !== 'undefined' ? window : this, function (window, isGlobal) {
    if (isGlobal) {
        (typeof (canvas) === 'undefined') && (window.canvas = {});
        (typeof (stage) === 'undefined') && (window.stage = {});
        (typeof (exportRoot) === 'undefined') && (window.exportRoot = {});
        (typeof (anim_container) === 'undefined') && (window.anim_container = {});
        (typeof (fnStartAnimation) === 'undefined') && (window.fnStartAnimation = () => {
        });
    }

    const helpersCore = (new HelpersCore());

    /**
     *
     * @param composition
     * @param callback
     */
    function handleClassLoad(composition, callback) {
        const
            app = document.getElementById('app'),
            full_screen = Boolean(app.getAttributeNode('data-full-screen'));

        /**
         *
         * @param main
         * @param props
         */
        function onLoadMain(main, props = {}) {
            main.setStates({
                ...props,
                composition: {[`_this_${library_name}`]: composition}
            });
            createjs.Ticker.removeEventListener('tick', allTapLoad);
            (typeof (callback) === 'function') && callback(main);
        }

        function allTapLoad() {
            if (typeof (AllTap) !== 'undefined') {
                onLoadMain(AllTap, {
                    full_screen: full_screen
                });
            } else if (typeof (parent.AllTap) !== 'undefined') {
                onLoadMain(parent.AllTap);
                window.AllTap = parent.AllTap;
                window.StageClass = parent.StageClass;
            }
        }

        createjs.Ticker.addEventListener('tick', allTapLoad);
    }

    /**
     *
     * @param event
     * @param composition
     */
    function handleFileLoad(event, composition) {
        let images = composition.getImages();

        if (!!event && (event.item.type === 'image')) {
            images[event.item.id] = event.result;
        }
    }

    /**
     * This function is always called, irrespective of the content. You can use the variable "stage" after it is created in token create_stage.
     * @param event
     * @param composition
     * @param dom_container
     */
    function handleComplete(event, composition, dom_container) {
        const
            preload_animate = helpersCore.getArray(dom_container, 'preload_animate', 'id'),
            library = composition.getLibrary(),
            ssMetadata = library.ssMetadata,
            {properties: {id, fps, width, height}} = library;

        if (helpersCore.isArrayIf(ssMetadata)) {
            let
                sprite_sheet = composition.getSpriteSheet(),
                {target} = event;

            for (let i = 0; i < ssMetadata.length; i++) {
                sprite_sheet[ssMetadata[i].name] = (new createjs.SpriteSheet({
                    'images': [target.getResult(ssMetadata[i].name)],
                    'frames': ssMetadata[i].frames
                }));
            }
        }

        window.exportRoot = (new library[library_name]());
        window.stage = (new library.Stage(canvas));
        stage.enableMouseOver();

        /**
         * Registers the "tick" event listener.
         */
        window.fnStartAnimation = function () {
            stage.addChild(exportRoot);
            createjs.Ticker.framerate = fps;
            createjs.Ticker.addEventListener('tick', stage);
        };

        /**
         * Code to support hidpi screens and responsive scaling.
         */
        AdobeAn.makeResponsive(make.responsive_active, make.responsive_replies, make.scale_active, make.scale_type, dom_container);

        AdobeAn.compositionLoaded(id);

        handleClassLoad(composition, (main) => {
            fnStartAnimation();

            const resizeOverlayContainer = () => {
                let
                    load_iframe = document.getElementById('load_iframe'),
                    dom_overlay_container = document.getElementById('dom_overlay_container'),
                    gallery_overlay_container = document.getElementById('gallery_overlay_container'),
                    close_overlay_container = document.getElementById('close_overlay_container'),
                    clientWidth = window.anim_container.clientWidth,
                    clientHeight = window.anim_container.clientHeight,
                    is_gallery_overlay = main.isElement(gallery_overlay_container),
                    is_dom_overlay = (main.isElement(dom_overlay_container) && main.isElement(close_overlay_container));

                if (main.isElement(load_iframe)) {
                    load_iframe.width = main.calcSize(width, clientWidth, iframe.size.width, false, 'int');
                    load_iframe.height = main.calcSize(height, clientHeight, iframe.size.height, false, 'int');
                }

                if (is_gallery_overlay || is_dom_overlay) {
                    let
                        width_size = main.calcSize(width, clientWidth, width, false, 'int'),
                        height_size = main.calcSize(height, clientHeight, height, false, 'int');

                    if (is_gallery_overlay) {
                        gallery_overlay_container.setAttribute('style', `width: ${width_size}px; height: ${height_size}px;`);
                    }

                    if (is_dom_overlay) {
                        dom_overlay_container.width = width_size;
                        dom_overlay_container.height = height_size;
                        close_overlay_container.width = main.calcSize(width, clientWidth, 40, false, 'int');
                        close_overlay_container.height = main.calcSize(height, clientHeight, 40, false, 'int');
                    }
                }
            };

            main.on('process_end', () => {
                resizeOverlayContainer();

                const resizeObserver = (new ResizeObserver(() => {
                    resizeOverlayContainer();
                    window.dispatchEvent(new Event('resize'));
                }));

                resizeObserver.observe(anim_container);

                main.isElement(preload_animate) && preload_animate.setAttribute('style', 'display: none;');
            }, library_name);
        });
    }

    /**
     * Initial application.
     */
    function init() {
        window.canvas = document.getElementById('canvas');
        window.anim_container = document.getElementById('animation_container');

        let
            dom_container = [canvas, anim_container],
            composition = AdobeAn.getComposition(helpersCore.keyObject(AdobeAn.compositions)),
            library = composition.getLibrary(),
            loader = (new createjs.LoadQueue(false)),
            app = document.getElementById('app'),
            preload = Boolean(app.getAttributeNode('data-preload')),
            {properties: {manifest}} = library;

        if (((library_name !== 'index') || (single)) && preload) {
            let
                element_div = document.createElement('DIV'),
                element_img = document.createElement('IMG');

            element_div.setAttribute('id', 'preload_animate');
            element_img.setAttribute('src', `${helpersCore.$base_uri || domain}/${web.uri_images}/pre-loader.gif`);

            element_div.appendChild(element_img);

            dom_container.push(element_div)

            app.appendChild(element_div);
        }

        if (library_name === 'index' && !single) {
            let element_iframe = document.createElement('IFRAME');

            element_iframe.setAttribute('src', `${helpersCore.$base_uri || domain}/${web.uri_src}/${web.uri_cover}.html`);
            element_iframe.setAttribute('id', 'load_iframe');
            element_iframe.setAttribute('allowfullscreen', '0');
            element_iframe.setAttribute('scrolling', 'no');
            element_iframe.setAttribute('frameborder', '0');

            /**
             * Remove effect animate.css
             */
            anim_container.removeAttribute('class');

            app.setAttribute('class', 'background-main');

            anim_container.appendChild(element_iframe);
        }

        loader.addEventListener('complete', function (event) {
            handleComplete(event, composition, dom_container);
        });

        if (helpersCore.isArrayIf(manifest)) {
            for (let property of manifest) {
                property.src = `${helpersCore.$base_uri || domain}/${web.uri_images_animate}/${property.src.replace('./images/', `${library_name}/`)}`;
            }

            /* Guide */
            createjs.MotionGuidePlugin.install();

            loader.addEventListener('fileload', function (event) {
                handleFileLoad(event, composition);
            });

            loader.loadManifest(manifest);
        } else {
            loader.dispatchEvent(new Event('complete'));
        }
    }

    window.addEventListener('load', init);
});
